/**
 * The menu: nine workspaces in an icon rail, and the open workspace's groups beside it (D19 phase 21, the v6 kit's
 * NAVIGATION.md and the owner's reference boards). The look is D19's: the glass chrome, the icons in circles that fill
 * when current, the folding headings.
 *
 * Nothing here is hand-listed. Screens come from the registry (`@rtcom/domain`), their place from `workspaces.ts`; a
 * screen's id and address never change with its place. v7 (CORE-1, Part E): only built screens are listed — a screen
 * not built yet is on Admin › Roadmap, not in the menu — under Part E's names; a record page or a merged screen lights
 * the list or hub it belongs to.
 *
 * Navigation is permission-aware: a workspace with nothing the person may see is not shown. That is presentation, not
 * enforcement — RLS decides the data.
 */
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isBuilt } from '../routes/implementedScreens';
import { NAV_SCREENS, NAV_SECTIONS, WORKSPACES, menuLabel, menuScreenOf, workspaceOf, type NavScreen, type Workspace } from '@rtcom/domain';
import { ChevronDown, CornerDownRight, PanelLeftClose, PanelLeftOpen, Pin, PinOff } from 'lucide-react';
import { usePreferences, useSavePreferences } from '../lib/preferences';
import { SECTION_ICON, screenIcon } from './navIcons';
import { useNavLang } from './navLang';
import { useSession } from '../lib/session';
import { cx } from '../ui/primitives';

const readOpen = (key: string): string[] => { try { return JSON.parse(localStorage.getItem(key) ?? '[]') as string[]; } catch { return []; } };
const SCREEN = new Map(NAV_SCREENS.map((s) => [s.id, s]));
const SECTION_ICON_NAME = new Map(NAV_SECTIONS.map((s) => [s.id, s.icon]));

/** The screen the address belongs to: the longest registered path that the address starts with. */
export function screenAt(pathname: string): NavScreen | undefined {
  let best: NavScreen | undefined;
  for (const s of NAV_SCREENS) {
    const pattern = s.path.replace(/:[^/]+/g, '[^/]+');
    if (new RegExp(`^${pattern}(/|$)`).test(pathname) && (!best || s.path.length > best.path.length)) best = s;
  }
  return best;
}

interface Row { screen: NavScreen; link: boolean }
interface ShownGroup { label: string; labelHi?: string; rows: Row[] }
interface ShownWorkspace { ws: Workspace; groups: ShownGroup[]; first: NavScreen | undefined }

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { can } = useSession();
  const [lang] = useNavLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Headings folded away by the person.
  const [folded, setFolded] = useState<string[]>(() => readOpen('rtcom.folded'));
  const flip = (key: string, set: typeof setFolded) => (id: string) => set((cur) => {
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage blocked */ }
    return next;
  });
  const toggleFold = flip('rtcom.folded', setFolded);
  // Pinned items (v7 Part D §10.6): up to six, kept with the person's preferences, shown at the top of every menu.
  const prefs = usePreferences();
  const savePrefs = useSavePreferences();
  const pinned = (prefs.data?.pinned ?? []).filter((id) => SCREEN.has(id) && isBuilt(id) && can(SCREEN.get(id)!.permission));
  const togglePin = (id: string) => {
    const next = pinned.includes(id) ? pinned.filter((x) => x !== id) : [...pinned, id].slice(-6);
    void savePrefs({ pinned: next }).catch(() => undefined);
  };

  const workspaces = useMemo<ShownWorkspace[]>(() => {
    const visible = (id: string) => { const s = SCREEN.get(id); return s && !s.hidden && can(s.permission) ? s : undefined; };
    return WORKSPACES.map((ws) => {
      const groups = ws.groups.map((g) => {
        const rows: Row[] = [];
        for (const id of g.screens) { const s = visible(id); if (s && isBuilt(s.id)) rows.push({ screen: s, link: false }); }
        for (const id of g.links ?? []) { const s = visible(id); if (s && isBuilt(s.id)) rows.push({ screen: s, link: true }); }
        return { label: g.label, labelHi: g.labelHi, rows };
      }).filter((g) => g.rows.length > 0);
      return { ws, groups, first: groups[0]?.rows[0]?.screen };
    }).filter((w) => w.first);
  }, [can]);

  // The workspace shown: the one the current screen lives in, unless the person opened another from the rail. The item
  // lit: the screen itself, or the list or hub a record page or merged screen belongs to.
  const hereId = screenAt(pathname)?.id ?? '';
  const here = workspaceOf(hereId)?.id;
  const current = menuScreenOf(hereId);
  const [browsing, setBrowsing] = useState<string | undefined>(undefined);
  useEffect(() => { setBrowsing(undefined); }, [pathname]);
  const openId = browsing ?? here ?? workspaces[0]?.ws.id;
  const open = workspaces.find((w) => w.ws.id === openId) ?? workspaces[0];
  const say = (en: string, hi?: string) => (lang === 'hi' && hi ? <span lang="hi">{hi}</span> : en);

  const item = (row: Row) => {
    const { screen } = row;
    const Icon = screenIcon(screen.label, SECTION_ICON_NAME.get(screen.section) ?? 'gauge');
    return (
      <li key={`${screen.id}${row.link ? '-link' : ''}`}>
        <NavLink
          to={screen.path}
          end
          title={screen.description}
          aria-current={screen.id === current ? 'page' : undefined}
          className={() => cx(
            'group/item flex h-[30px] items-center gap-2 border-l-2 pl-[8px] pr-3 text-body transition-colors duration-[120ms]',
            screen.id === current && !row.link ? 'border-brand bg-brand-soft font-medium text-brand' : 'border-transparent text-ink-muted hover:bg-surface-sunken hover:text-ink',
          )}
        >
          {() => (<>
            <span aria-hidden className={cx('nav-icon flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full transition-colors',
              screen.id === current && !row.link ? 'bg-brand text-on-brand' : 'text-ink-faint')}>
              {row.link ? <CornerDownRight size={12} /> : <Icon size={13} />}
            </span>
            <span className="truncate">{menuLabel(screen)}</span>
            <span role="button" tabIndex={-1} aria-label={pinned.includes(screen.id) ? `Unpin ${menuLabel(screen)}` : `Pin ${menuLabel(screen)} to the top`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(screen.id); }}
              className={cx('ml-auto shrink-0 rounded p-0.5 text-ink-faint hover:text-ink', pinned.includes(screen.id) ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100')}>
              {pinned.includes(screen.id) ? <PinOff size={11} aria-hidden /> : <Pin size={11} aria-hidden />}
            </span>
            {row.link ? <span className="sr-only"> (its home is elsewhere in the menu)</span> : null}
          </>)}
        </NavLink>
      </li>
    );
  };

  return (
    <nav
      aria-label="Main"
      className={cx(
        'shell-chrome flex shrink-0 flex-col border-r border-line bg-surface transition-[width] duration-200',
        collapsed ? 'w-[64px]' : 'w-[284px]',
      )}
    >
      {/* No line under the logo: the panel below carries the one line of the menu (the owner, 2026-10-01). */}
      <div className="flex h-12 items-center gap-2 px-3">
        {/* The RTCOM wordmark alone heads the menu (the owner, 2026-10-02): its shape cut from the supplied logo, filled
            with the theme's brand colour; big while the menu is open, and it shrinks with the menu to fit the rail. */}
        <span role="img" aria-label="RTCOM ERP"
          className={cx('shell-wordmark block shrink-0 bg-brand transition-[width,height] duration-200',
            collapsed ? 'h-[11px] w-[40px]' : 'h-[44px] w-[160px]')} />
        <button type="button" onClick={onToggle} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          className={cx('rounded-[var(--radius-control)] p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink', collapsed ? 'hidden' : 'ml-auto')}>
          <PanelLeftClose size={16} />
        </button>
      </div>

      {workspaces.length === 0 ? (
        <p className={cx('px-3 py-4 text-small text-ink-muted', collapsed && 'hidden')}>No screens are available to this account yet.</p>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* The rail: the nine workspaces, Admin at the foot (the kit's order). */}
          <div role="tablist" aria-orientation="vertical" aria-label="Workspaces"
            className="nav-rail flex w-[64px] shrink-0 flex-col items-center gap-1 overflow-y-auto py-2">
            {workspaces.map(({ ws, first }) => {
              const Icon = SECTION_ICON[ws.icon];
              const isOpen = open?.ws.id === ws.id;
              const isHere = here === ws.id;
              return (
                <button key={ws.id} type="button" role="tab" aria-selected={isOpen}
                  aria-label={`${ws.label}${isHere ? ' (you are here)' : ''}`} title={ws.label}
                  onClick={() => { if (collapsed) { if (first) navigate(first.path); } else setBrowsing(ws.id); }}
                  className={cx('group flex w-[56px] flex-col items-center gap-0.5 rounded-[var(--radius-control)] py-1 text-[11px] leading-tight', ws.id === 'admin' && 'mt-auto',
                    isOpen && !collapsed ? 'text-brand' : 'text-ink-muted hover:text-ink')}>
                  <span className={cx('nav-dot flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                    isHere ? 'bg-brand text-on-brand shadow-[0_2px_8px_-2px_var(--color-brand)]' : isOpen && !collapsed ? 'bg-brand-soft text-brand' : 'group-hover:bg-surface-sunken')}>
                    {Icon ? <Icon size={16} aria-hidden /> : ws.label.slice(0, 1)}
                  </span>
                  <span className="max-w-full truncate px-0.5">{say(ws.label, ws.labelHi)}</span>
                </button>
              );
            })}
            {collapsed ? (
              <button type="button" onClick={onToggle} aria-label="Expand navigation" className="mt-2 rounded-[var(--radius-control)] p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink">
                <PanelLeftOpen size={16} />
              </button>
            ) : null}
          </div>

          {/* The open workspace: its groups in Part E's order. */}
          {!collapsed && open ? (
            // One line, rising beside the rail and curving to the right along the top: the same inner curve as the
            // content well (the owner, 2026-10-01). Square in the classic look, where --radius-shell is 0.
            <div role="tabpanel" aria-label={open.ws.label}
              className="nav-well min-w-0 flex-1 overflow-y-auto rounded-tl-[var(--radius-shell)] py-2 shadow-[inset_1px_1px_0_var(--color-line)]">
              <p className="px-3 pb-2 text-section font-semibold text-ink">{say(open.ws.label, open.ws.labelHi)}</p>
              {pinned.length ? (
                <div className="mb-2 border-b border-line pb-2">
                  <p className="nav-section label-text px-3 pb-1">{lang === 'hi' ? <span lang="hi">पिन किए</span> : 'Pinned'}</p>
                  <ul>{pinned.map((id) => item({ screen: SCREEN.get(id)!, link: false }))}</ul>
                </div>) : null}
              {open.groups.map((g, gi) => {
                const key = `${open.ws.id}:${g.label}`;
                const holdsCurrent = g.rows.some((r) => !r.link && r.screen.id === current);
                return (
                  <div key={`${key}:${gi}`} className="mb-2">
                    {g.label ? (
                      <button type="button" onClick={() => toggleFold(key)} aria-expanded={!folded.includes(key)}
                        className="nav-section label-text flex w-full items-center gap-1.5 px-3 pb-1 text-left hover:text-ink">
                        <span className="flex-1 truncate">{say(g.label, g.labelHi)}</span>
                        <ChevronDown size={12} aria-hidden className={cx('shrink-0 transition-transform', folded.includes(key) && '-rotate-90')} />
                      </button>
                    ) : null}
                    <ul hidden={Boolean(g.label) && folded.includes(key) && !holdsCurrent}>{g.rows.map((r) => item(r))}</ul>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </nav>
  );
}

